<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface Props {
  /** URL изображения. При отсутствии/ошибке загрузки показываются инициалы. */
  src?: string | null;
  /** Имя — источник инициалов и детерминированного градиента fallback. */
  name: string;
  /** Диаметр аватара в пикселях. */
  sizePx?: number;
  /** Явное переопределение инициалов (по умолчанию вычисляются из name). */
  fallback?: string;
}

const props = withDefaults(defineProps<Props>(), {
  src: null,
  sizePx: 40,
  fallback: undefined,
});

// Приглушённые градиенты (индиго/тил/роза/фиолет/охра/шалфей/серо-фиолет).
const GRADIENTS: readonly (readonly [string, string])[] = [
  ['#4f5bd5', '#6a4fd0'],
  ['#2f8f83', '#3aa89a'],
  ['#b3577a', '#c76b8e'],
  ['#7c5cbf', '#9a6fd0'],
  ['#b8823a', '#cf9a4a'],
  ['#5f8f6a', '#74a37e'],
  ['#6b6f8f', '#8288a8'],
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
};

const initials = computed<string>(() => {
  if (props.fallback) {
    return props.fallback;
  }
  const words = props.name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
});

const gradient = computed<string>(() => {
  const pair = GRADIENTS[hashString(props.name) % GRADIENTS.length] ?? GRADIENTS[0]!;
  return `linear-gradient(135deg, ${pair[0]}, ${pair[1]})`;
});

// Масштаб инициалов от диаметра: у мелких аватаров (14–20px) две буквы должны
// умещаться внутри круга, поэтому без крупного нижнего порога.
const fontSizePx = computed<number>(() => Math.max(5, Math.round(props.sizePx * 0.34)));

// Fallback при ошибке загрузки изображения (сбрасывается при смене src).
const imageFailed = ref(false);
watch(
  () => props.src,
  () => {
    imageFailed.value = false;
  },
);
const showImage = computed<boolean>(() => !!props.src && !imageFailed.value);
</script>

<template>
  <div
    class="relative shrink-0"
    :style="{ width: `${sizePx}px`, height: `${sizePx}px` }"
  >
    <img
      v-if="showImage"
      :src="src ?? ''"
      :alt="name"
      class="h-full w-full rounded-full object-cover"
      @error="imageFailed = true"
    />
    <div
      v-else
      class="flex h-full w-full items-center justify-center overflow-hidden rounded-full font-semibold text-white"
      :style="{ backgroundImage: gradient, fontSize: `${fontSizePx}px`, lineHeight: '1' }"
    >
      {{ initials }}
    </div>

    <!-- Внутреннее кольцо-разделитель поверх аватара. -->
    <span
      class="app-avatar__ring pointer-events-none absolute inset-0 rounded-full"
      aria-hidden="true"
    ></span>

    <!-- Бейджи (каналы/статус) в правом нижнем углу. -->
    <span
      v-if="$slots.badge"
      class="absolute right-0 bottom-0 flex items-center"
    >
      <slot name="badge" />
    </span>
  </div>
</template>

<style scoped>
.app-avatar__ring {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
}
</style>
